import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Snackbar,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { API_BASE_URL, getApiBaseUrlWithFallback } from '../config/environment';

// Section Card wrapper component for view mode
function SectionCard({ title, icon, children, onEdit }) {
  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: 'primary.50',
              display: 'flex',
            }}>
              {icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          </Box>
          <Tooltip title="Edit">
            <IconButton 
              onClick={onEdit}
              size="small"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

// View Field component
function ViewField({ label, value, icon, isLink }) {
  const displayValue = value || '—';
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color: 'action.active' }}>{icon}</Box>}
        {isLink && value ? (
          <Link href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener">
            <Typography variant="body1">{displayValue}</Typography>
          </Link>
        ) : (
          <Typography variant="body1" sx={{ color: value ? 'text.primary' : 'text.secondary' }}>
            {displayValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function InterclubSettings({ interclubId }) {
  const { getIdToken } = useFirebaseAuth();
  
  const [interclub, setInterclub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  
  // Form state (Interclub Settings: only name, short name, description)
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    description: '',
  });

  const loadInterclub = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load interclub data');
      }
      
      const data = await response.json();
      setInterclub(data);
      setFormData({
        name: data.name || '',
        shortName: data.shortName || '',
        description: data.description || '',
      });
    } catch (err) {
      console.error('Error loading interclub:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId]);

  useEffect(() => {
    loadInterclub();
  }, [loadInterclub]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/interclubs/${interclubId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          shortName: formData.shortName,
          description: formData.description,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }
      
      setSuccess('Settings saved successfully!');
      setEditingSection(null);
      await loadInterclub();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (interclub) {
      setFormData({
        name: interclub.name || '',
        shortName: interclub.shortName || '',
        description: interclub.description || '',
      });
    }
    setEditingSection(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Edit Section Card wrapper
  const EditSectionCard = ({ title, icon, children, section }) => (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, borderColor: 'primary.main', borderWidth: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: 'primary.100',
              display: 'flex',
            }}>
              {icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            <Chip label="Editing" size="small" color="primary" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleCancelEdit}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => handleSave()}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
          }}>
            <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Interclub Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Name, short name, and description for your interclub
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Basic Information (only section for Interclub Settings) */}
        {editingSection === 'basic' ? (
          <EditSectionCard title="Basic Information" icon={<InfoIcon color="primary" />} section="basic">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Interclub Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder="e.g., Pacific Northwest Interclub"
                  helperText="The full name of your interclub"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Short Name"
                  value={formData.shortName}
                  onChange={handleChange('shortName')}
                  placeholder="e.g., PNIC"
                  helperText="Abbreviation or short name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder="A brief description of your interclub organization..."
                  helperText="Describe your interclub's mission and purpose"
                />
              </Grid>
            </Grid>
          </EditSectionCard>
        ) : (
          <SectionCard title="Basic Information" icon={<InfoIcon color="primary" />} onEdit={() => setEditingSection('basic')}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <ViewField label="Interclub Name" value={formData.name} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ViewField label="Short Name" value={formData.shortName} />
              </Grid>
              <Grid item xs={12}>
                <ViewField label="Description" value={formData.description} />
              </Grid>
            </Grid>
          </SectionCard>
        )}
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: 2 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
